// src/services/adminService.js
//
// Consume los 4 endpoints de /api/admin/users
// Reemplaza todos los datos hardcodeados de OverviewTab.jsx
// ─────────────────────────────────────────────────────────────────────────

// ── A QUE SERVIDOR HABLA EL PANEL ───────────────────────────────────────────
//
// Antes esto era:
//
//     const API_BASE = import.meta.env.VITE_API_URL || 'https://spacy-backend.onrender.com';
//
// Es decir: si `VITE_API_URL` no estaba puesta, el panel de administracion se
// conectaba a PRODUCCION. Y no lo decia por ningun lado.
//
// Eso significa que levantar el proyecto en local sin esa variable te sienta
// delante de los datos reales de los alumnos creyendo que estas en tu maquina.
// Mirar ya seria bastante malo; desde este panel tambien se editan usuarios,
// planes y cobros. Un fallo que no da error y que solo se nota cuando ya has
// tocado algo.
//
// El resto de la aplicacion usa rutas relativas —`/api/...`— y deja que el
// proxy de Vite decida. Este fichero era el unico que se salia. Ahora hace lo
// mismo que los demas: si quieres apuntar a produccion, lo dices a proposito
// poniendo `VITE_API_URL`, y entonces es una decision y no un descuido.
const API_BASE = import.meta.env.VITE_API_URL || '';
const BASE = `${API_BASE}/api/admin/users`;

function authHeaders() {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// ── EL FRENO DEL SERVIDOR NO ES UNA AVERÍA ──────────────────────────────────
// Los KPIs llevan un freno de 250 ms entre peticiones. Al abrir la pestaña,
// React (en desarrollo) monta el componente dos veces y pide los KPIs dos
// veces seguidas: la segunda recibía un 429 y el Overview entero se caía con
// «Error al cargar el dashboard · [object Object]» (22-09-2026). Un 429 dice
// «espera un momento», así que se espera lo que pide y se reintenta una vez.
//
// Y el texto del error: `detail` puede ser un objeto ({error, message}); meterlo
// tal cual en `new Error` pintaba «[object Object]», que no dice nada.
async function apiFetch(path, reintento = true) {
  const res = await fetch(path, { headers: authHeaders() });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    if (res.status === 429 && reintento) {
      const segundos = Math.min(Number(err.detail?.retry_after) || 1, 5);
      await new Promise((ok) => setTimeout(ok, segundos * 1000));
      return apiFetch(path, false);
    }
    const detalle = typeof err.detail === "string" ? err.detail : err.detail?.message;
    throw new Error(detalle || `HTTP ${res.status} — ${path}`);
  }
  return res.json();
}


// ─────────────────────────────────────────────────────────────────────────
// 1. KPIs globales  →  reemplaza GLOBAL
// ─────────────────────────────────────────────────────────────────────────
export async function fetchGlobalKpis() {
  return apiFetch(`${BASE}/kpis`);
}


// ─────────────────────────────────────────────────────────────────────────
// 2. Lista de alumnos  →  reemplaza STUDENTS (shape mínima para sidebar)
// ─────────────────────────────────────────────────────────────────────────
export async function fetchStudentList(incluir = "alumnos") {
  // `incluir`: "alumnos" | "equipo" | "todos". El servidor solo acepta esos
  // tres, asi que un valor raro devuelve 422 en vez de una lista silenciosamente
  // distinta de la que se pidio.
  return apiFetch(`${BASE}/?incluir=${encodeURIComponent(incluir)}`);
}


// ─────────────────────────────────────────────────────────────────────────
// 3. Detalle completo de un alumno  →  reemplaza STUDENTS[i] (todas las tabs)
// ─────────────────────────────────────────────────────────────────────────
export async function fetchStudentDetail(userId) {
  return apiFetch(`${BASE}/${userId}/detail`);
}


// ─────────────────────────────────────────────────────────────────────────
// 4. Sesiones SpaCy  →  reemplaza SD[sel.id] en SpacySessionPanel
//    Devuelve un array; los timestamps vienen como ISO string,
//    esta función los convierte a Date para que SessionRow los use igual.
// ─────────────────────────────────────────────────────────────────────────
export async function fetchSpacySessions(userId) {
  const sessions = await apiFetch(`${BASE}/${userId}/spacy-sessions`);
  return sessions.map((s) => ({
    ...s,
    at: new Date(s.at),   // ISO → Date (igual que el hardcodeado usaba new Date())
  }));
}


// ─────────────────────────────────────────────────────────────────────────
// 5. Combates de Spacy Arena
//    La Arena no guardaba nada: calculaba el veredicto al cerrar la sesion,
//    se lo enseñaba al alumno una vez y lo tiraba. Ahora tiene tabla propia,
//    y esto es lo que la lee.
// ─────────────────────────────────────────────────────────────────────────
export async function fetchArenaSessions(userId) {
  const sesiones = await apiFetch(`${BASE}/${userId}/arena-sessions`);
  return sesiones.map((s) => ({ ...s, at: s.at ? new Date(s.at) : null }));
}
